"use client";

import { Bell, AlertCircle, MessageSquare, UserPlus, Clock, Settings, FileText } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/hooks/useNotifications";
import { useSound } from "@/hooks/useSound";
import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

const TIPO_CONFIG = {
  asignacion: { icon: UserPlus, color: "text-blue-500" },
  error: { icon: AlertCircle, color: "text-red-500" },
  comentario: { icon: MessageSquare, color: "text-green-500" },
  sla: { icon: Clock, color: "text-amber-500" },
  sistema: { icon: Settings, color: "text-slate-500" },
  documentos: { icon: FileText, color: "text-emerald-600" },
};

function timeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return "Ahora";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d`;
}

export function NotificationBell() {
  const { notificaciones, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { playNotification } = useSound();
  const prevCountRef = useRef(unreadCount);
  const router = useRouter();

  useEffect(() => {
    if (unreadCount > prevCountRef.current && prevCountRef.current !== 0) {
      playNotification();
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount, playNotification]);

  const handleNotificationClick = (notif: { id: string; leida: boolean; solicitudId?: string }) => {
    if (!notif.leida) markAsRead(notif.id);
    if (notif.solicitudId) {
      router.push(`/emision/${notif.solicitudId}`);
    }
  };

  return (
    <Popover>
      <PopoverTrigger className="relative p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
        <Bell className="h-5 w-5 text-slate-600 dark:text-slate-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="text-sm font-semibold">Notificaciones</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] text-muted-foreground"
              onClick={markAllAsRead}
            >
              Marcar todas leídas
            </Button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {notificaciones.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Sin notificaciones
            </div>
          ) : (
            notificaciones.slice(0, 20).map((notif) => {
              const config = TIPO_CONFIG[notif.tipo] || TIPO_CONFIG.sistema;
              const Icon = config.icon;
              return (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`flex items-start gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors
                    ${notif.leida ? "opacity-60" : "bg-blue-50/50 dark:bg-blue-900/10"}
                    hover:bg-slate-50 dark:hover:bg-slate-800/50
                  `}
                >
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium truncate">{notif.titulo}</p>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">{timeAgo(notif.fecha)}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">{notif.mensaje}</p>
                    {notif.solicitudId && (
                      <span className="text-[10px] text-segurclassic-gold mt-0.5 inline-block">
                        Ver solicitud →
                      </span>
                    )}
                  </div>
                  {!notif.leida && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                  )}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
